import React from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Table,
  Spinner,
  Alert,
} from "react-bootstrap";
import _ from "lodash";
import { Link } from "react-router-dom";
import { connect } from "react-redux";

import NavBar from "../../Components/NavBar";
import AppComponent from "../../../Components/AppComponent";
import api from "../../../Services/Api/api";
import { impersonate } from "../../../Modules/auth/startup";
import { formatPhone } from "../../../Modules/utils/etc";

const SCREEN_NAME = "OFFICES_SCREEN";

class OfficesScreen extends AppComponent {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: null,
      modalError: null,
      users: [],
      activeRole: "office",
      showModal: false,
      modalMode: "add",
      selectedUser: null,
      form: OfficesScreen.getEmptyForm("office"),
    };
  }

  static getEmptyForm(role = "office") {
    return {
      username: "",
      password: "",
      firstName: "",
      lastName: "",
      role,
      phone: "",
      email: "",
      address: "",
      status: "active",
      agent:
        role === "office"
          ? {
              vendorName: "",
              rates: {
                pickup: "",
                delivery: "",
              },
            }
          : null,
    };
  }

  componentDidMount() {
    this.loadUsers();
  }

  async loadUsers() {
    try {
      const { activeRole } = this.state;
      this.setState({ loading: true, error: null });

      const res = await api.get("v1/head-office/user", {
        queryStr: JSON.stringify({ where: { role: activeRole } }),
      });

      this.setState({ users: res?.users || [], loading: false });
    } catch (err) {
      this.setState({
        error: err.message || "Failed to load users",
        loading: false,
      });
    }
  }

  generateSystemUsername() {
    return `emp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  generateSystemPassword() {
    return Math.random().toString(36).slice(2, 12);
  }

  openModal = (mode, user = null) => {
    const { activeRole } = this.state;
    const emptyForm = OfficesScreen.getEmptyForm(activeRole);

    const form = user
      ? {
          ...emptyForm,
          ...user,
          agent:
            activeRole === "office"
              ? {
                  vendorName: user?.agent?.vendorName || "",
                  rates: {
                    pickup: user?.agent?.rates?.pickup ?? "",
                    delivery: user?.agent?.rates?.delivery ?? "",
                  },
                }
              : null,
          password: "",
        }
      : emptyForm;

    this.setState({
      showModal: true,
      modalMode: mode,
      selectedUser: user,
      form,
      modalError: null,
    });
  };

  closeModal = () => {
    const { activeRole } = this.state;
    this.setState({
      showModal: false,
      modalError: null,
      selectedUser: null,
      form: OfficesScreen.getEmptyForm(activeRole),
    });
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((s) => ({
      form: { ...s.form, [name]: value },
    }));
  };

  handleAgentChange = (field, value) => {
    this.setState((s) => ({
      form: {
        ...s.form,
        agent: {
          ...s.form.agent,
          ...(field === "vendorName" && { vendorName: value }),
          rates: {
            ...s.form.agent.rates,
            ...(field === "pickup" && { pickup: value }),
            ...(field === "delivery" && { delivery: value }),
          },
        },
      },
    }));
  };

  normalizePayload(form) {
    if (form.role === "employee") {
      return { ...form, agent: null };
    }

    return {
      ...form,
      agent: {
        vendorName: form.agent.vendorName || null,
        rates: {
          pickup:
            form.agent.rates.pickup === ""
              ? null
              : Number(form.agent.rates.pickup),
          delivery:
            form.agent.rates.delivery === ""
              ? null
              : Number(form.agent.rates.delivery),
        },
      },
    };
  }

  handleSave = async () => {
    const { modalMode, selectedUser, form, activeRole } = this.state;

    try {
      this.setState({ loading: true, modalError: null });

      let payload = this.normalizePayload(form);

      if (activeRole === "employee" && modalMode === "add") {
        payload.username = this.generateSystemUsername();
        payload.password = this.generateSystemPassword();
      }

      if (modalMode === "add") {
        await api.post("v1/head-office/user", {
          ...payload,
          office: this.props.user._id,
        });
      }

      if (modalMode === "edit" && selectedUser?._id) {
        await api.patch(
          `v1/head-office/user/${selectedUser._id}`,
          _.pick(payload, [
            "username",
            "firstName",
            "lastName",
            "phone",
            "email",
            "address",
            "status",
            "agent",
          ])
        );
      }

      this.closeModal();
      await this.loadUsers();
    } catch (err) {
      this.setState({ modalError: err.message || "Failed to save user" });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleChangePassword = async () => {
    const { selectedUser, form } = this.state;

    if (selectedUser?.role === "employee") {
      this.setState({ modalError: "Employees do not have login access." });
      return;
    }

    if (!form.password) {
      this.setState({ modalError: "Please enter a new password." });
      return;
    }

    try {
      this.setState({ loading: true, modalError: null });
      await api.put(`v1/head-office/user/${selectedUser._id}/password`, {
        password: form.password,
      });
      this.closeModal();
    } catch (err) {
      this.setState({
        modalError: err.message || "Failed to change password",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      this.setState({ loading: true });
      await api.delete(`v1/head-office/user/${userId}`);
      await this.loadUsers();
    } catch (err) {
      this.setState({ error: err.message || "Failed to delete user" });
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const {
      loading,
      error,
      modalError,
      users,
      showModal,
      modalMode,
      form,
      selectedUser,
      activeRole,
    } = this.state;

    return (
      <>
        <NavBar activeLink="/offices" />
        <Container fluid className="py-4 px-3">
          <Row className="mb-3">
            <Col>
              <Button
                className="me-2"
                variant={
                  activeRole === "office" ? "primary" : "outline-primary"
                }
                onClick={() =>
                  this.setState({ activeRole: "office" }, this.loadUsers)
                }
              >
                Offices
              </Button>
              <Button
                variant={
                  activeRole === "employee" ? "primary" : "outline-primary"
                }
                onClick={() =>
                  this.setState({ activeRole: "employee" }, this.loadUsers)
                }
              >
                Employees
              </Button>
            </Col>
            <Col xs="auto">
              <Button onClick={() => this.openModal("add")}>
                + Add {activeRole === "office" ? "Office" : "Employee"}
              </Button>
            </Col>
          </Row>

          {loading && <Spinner animation="border" />}
          {error && <Alert variant="danger">{error}</Alert>}

          {!loading && users.length > 0 && (
            <Card>
              <Card.Body>
                <Table hover size="sm" responsive="sm">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Status</th>
                      {activeRole === "office" && <th>Vendor</th>}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-nowrap">
                    {users.map((u) => (
                      <tr key={u._id}>
                        <td>{u.role === "employee" ? "—" : u.username}</td>
                        <td>{`${u.firstName || ""} ${u.lastName || ""}`}</td>
                        <td>{formatPhone(u)}</td>
                        <td>{u.email || "—"}</td>
                        <td>{u.status}</td>
                        {activeRole === "office" && (
                          <td>
                            {u.agent?.vendorName || "-"}{" "}
                            <>
                              (pick: ₹{u.agent?.rates?.pickup || "-"}, del: ₹
                              {u.agent?.rates?.delivery} )
                            </>
                          </td>
                        )}
                        <td>
                          <Button
                            size="sm"
                            onClick={() => this.openModal("edit", u)}
                          >
                            Edit
                          </Button>{" "}
                          {u.role === "office" && (
                            <>
                              <Button
                                size="sm"
                                variant="warning"
                                onClick={() => this.openModal("password", u)}
                              >
                                Password
                              </Button>{" "}
                              <Link to="/">
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => impersonate(u._id)}
                                >
                                  Login
                                </Button>
                              </Link>{" "}
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => this.handleDelete(u._id)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {!loading && !error && users.length === 0 && (
            <Card className="text-center py-5">
              <Card.Body>
                <h5 className="text-muted mb-2">
                  No {activeRole === "office" ? "Offices" : "Employees"} Found
                </h5>
                <p className="text-muted mb-3">
                  {activeRole === "office"
                    ? "You have not added any offices yet."
                    : "You have not added any employees yet."}
                </p>
                <Button onClick={() => this.openModal("add")}>
                  + Add {activeRole === "office" ? "Office" : "Employee"}
                </Button>
              </Card.Body>
            </Card>
          )}

          <Modal show={showModal} onHide={this.closeModal} centered>
            <Modal.Header closeButton>
              <Modal.Title>
                {modalMode === "add"
                  ? `Add ${activeRole === "office" ? "Office" : "Employee"}`
                  : modalMode === "edit"
                  ? "Edit User"
                  : "Change Password"}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              {modalError && <Alert variant="danger">{modalError}</Alert>}

              {modalMode === "password" ? (
                <Form.Control
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={this.handleChange}
                />
              ) : (
                <>
                  {/* Username (Office only) */}
                  {activeRole === "office" && (
                    <Form.Control
                      className="mb-2"
                      placeholder="Username"
                      name="username"
                      value={form.username}
                      onChange={this.handleChange}
                    />
                  )}

                  {/* Password (Add Office only) */}
                  {activeRole === "office" && modalMode === "add" && (
                    <Form.Control
                      className="mb-2"
                      type="password"
                      placeholder="Password"
                      name="password"
                      value={form.password}
                      onChange={this.handleChange}
                    />
                  )}
                  <Form.Control
                    className="mb-2"
                    placeholder="First Name"
                    name="firstName"
                    value={form.firstName}
                    onChange={this.handleChange}
                  />
                  <Form.Control
                    className="mb-2"
                    placeholder="Last Name"
                    name="lastName"
                    value={form.lastName}
                    onChange={this.handleChange}
                  />
                  <Form.Control
                    className="mb-2"
                    placeholder="Email"
                    name="email"
                    value={form.email}
                    onChange={this.handleChange}
                  />
                  <Form.Control
                    className="mb-2"
                    placeholder="Phone"
                    name="phone"
                    value={form.phone}
                    onChange={this.handleChange}
                  />

                  {form.role === "office" && (
                    <>
                      <hr />
                      <h6 className="text-muted">Vendor Details</h6>
                      <Form.Control
                        className="mb-2"
                        placeholder="Vendor Name"
                        value={form.agent.vendorName}
                        onChange={(e) =>
                          this.handleAgentChange("vendorName", e.target.value)
                        }
                      />
                      <Row>
                        <Col>
                          <Form.Control
                            type="number"
                            placeholder="Pickup Rate"
                            value={form.agent.rates.pickup}
                            onChange={(e) =>
                              this.handleAgentChange("pickup", e.target.value)
                            }
                          />
                        </Col>
                        <Col>
                          <Form.Control
                            type="number"
                            placeholder="Delivery Rate"
                            value={form.agent.rates.delivery}
                            onChange={(e) =>
                              this.handleAgentChange("delivery", e.target.value)
                            }
                          />
                        </Col>
                      </Row>
                    </>
                  )}
                </>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={this.closeModal}>
                Cancel
              </Button>
              <Button variant="primary" onClick={this.handleSave}>
                Save
              </Button>
            </Modal.Footer>
          </Modal>
        </Container>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.pState.user.user,
});

export default connect(mapStateToProps)(OfficesScreen);
